import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import lodashWithout from 'lodash/fp/without';
import lodashUniq from 'lodash/fp/uniq';
import typeCheck from 'common/util/typeCheck';

import { UTILITY_USER_ROLE_ADMIN } from 'common/utility/userRoles';

import { UTILITY_PROVIDERS_MILES_RANGE } from 'common/utility/constants';
import {
  getManualMappingBlockedProviders,
  getBlockedProviderLabel,
  getBlockedProviderTitle,
} from 'common/utility/helpers';

import {
  SR_API_ACTION_UTILITY_PROVIDER_GET_ADDRESS_COVERAGE,
  SR_API_ACTION_UTILITY_PROVIDER_UPDATE_ADDRESS_COVERAGE,
  SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
  SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
  SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
  SR_API_ACTION_UTILITY_ACTIVATION_GET,
  SR_API_ACTION_ADDRESS_FCC_CONFIRM_MAPPING,
} from 'api/utilityActionTypes';

import { SCREEN_ID_UTILITY_PROFIT_ADMIN_PROCESSING_ADDRESS } from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import { withUtilityApiState } from 'client/helpers/withApiState';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';

import branch from 'client/core/branch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityActivationActions from 'client/actions/UtilityActivation';
import UtilityProviderActions from 'client/actions/UtilityProvider';
import { getRangeIdentifier } from 'client/actions/UtilityProvider/getMappedProvidersInRange';

import SrUtilityProfitAdminProcessAddressPage from 'client/components/SrUtilityProfitAdminProcessAddressPage/SrUtilityProfitAdminProcessAddressPage';
import UPProviderSearchForm from 'client/components/UPProviderSearchForm/UPProviderSearchForm';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PROFIT_ADMIN_PROCESSING_ADDRESS)
@withScrollToTop
@withUtilityApiState({
  getAddressCoverageApiState: {
    action: SR_API_ACTION_UTILITY_PROVIDER_GET_ADDRESS_COVERAGE,
  },
})
@branch(({ match }) => ({
  addressCoverage: ['utilityProviders', 'coverageByAddressId', match.params.addressId],
  utilityTypes: ['utilityActivations', 'types'],
}))
@branch(({ addressCoverage }) => {
  if (!addressCoverage?.id) {
    return {};
  }

  try {
    if (addressCoverage.latitude == null || addressCoverage.longitude == null) {
      return {
        utilityProvidersByAddress: ['utilityProviders', 'byAddressId', addressCoverage.id],
      };
    }

    const rangeIdentifier = getRangeIdentifier({
      latitude: addressCoverage.latitude,
      longitude: addressCoverage.longitude,
      rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
    });

    return {
      utilityProvidersInRange: ['utilityProviders', 'coverageByRange', rangeIdentifier],
      utilityProvidersByAddress: ['utilityProviders', 'byAddressId', addressCoverage.id],
    };
  } catch (err) {
    return {};
  }
})
class UtilityProfitAdminProcessingAddressPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Profit Admin | Process Address',
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    addressCoverage: PropTypes.object,
    utilityTypes: PropTypes.array,
    utilityProvidersInRange: PropTypes.array,
    getAddressCoverageApiState: PropTypes.object.isRequired,
  };

  static fetchData(match, inject) {
    return Promise.all([
      UtilityActivationActions.getTypesList(inject),
      UtilityProviderActions.getAddressCoverage(inject, {
        addressId: parseInt(match.params.addressId, 10),
      }).then((addressCoverage) => {
        if (addressCoverage) {
          return Promise.all([
            UtilityProviderActions.getMappedProvidersInRange(inject, {
              latitude: addressCoverage.latitude,
              longitude: addressCoverage.longitude,
              rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
              utilityTypeId: null,
            }),
            UtilityProviderActions.getListByAddressId(inject, {
              addressId: addressCoverage.id,
            }),
          ]);
        }
      }),
    ]);
  }

  constructor(props) {
    super(props);
    this.state = {
      serviceAreaProviderMappingArgs: null,
      serviceAreaProviderMappingIsAdding: false,
      manualMappingBlockModalArgs: null,
    };
  }

  componentDidMount() {
    const { match, dispatch, location, history } = this.props;
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_GET,
      payload: {
        addressId: parseInt(match.params.addressId, 10),
        archivedAt: null,
      },
    }).then((utilityActivation) => {
      if (utilityActivation && queryString.parse(location.search).redirect !== 'false') {
        history.replace(`/utility-profit/admin/processing/${utilityActivation.shortId}`);
      }
    });
  }

  updateProviderCoveragePoints = ({
    addressId,
    utilityTypeId,
    utilityProviderIds,
    coveragePointsConfig,
    mappingNotes,
    manualMappingBlockBypassed = false,
  }) => {
    typeCheck('addressId::PositiveNumber', addressId);
    typeCheck('utilityProviderIds::Array', utilityProviderIds);
    typeCheck('utilityTypeId::PositiveNumber', utilityTypeId);
    typeCheck('coveragePointsConfig::Maybe Array', coveragePointsConfig);
    typeCheck('mappingNotes::Maybe String', mappingNotes);

    if (!manualMappingBlockBypassed) {
      const { addressCoverage } = this.props;
      const currentItem = (addressCoverage?.items || []).find(
        (item) => item.type.id === utilityTypeId,
      );
      const currentProviderIds = (currentItem?.availableProviders || []).map(({ id }) => id);

      const blockedProviderIds = getManualMappingBlockedProviders({
        utilityTypeId,
        currentProviderIds,
        newProviderIds: utilityProviderIds,
      });

      if (blockedProviderIds.length > 0) {
        const blockedProviderActions = Object.fromEntries(
          blockedProviderIds.map((id) => [
            id,
            utilityProviderIds.includes(id) ? 'Mapping' : 'Unmapping',
          ]),
        );
        this.setState({
          manualMappingBlockModalArgs: {
            addressId,
            utilityTypeId,
            utilityProviderIds,
            coveragePointsConfig,
            mappingNotes,
            blockedProviderIds,
            blockedProviderActions,
          },
        });
        return Promise.resolve({ wasBlocked: true });
      }
    }

    const { dispatch } = this.props;
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PROVIDER_UPDATE_ADDRESS_COVERAGE,
      payload: {
        addressId,
        utilityTypeId,
        utilityProviderIds,
        coveragePointsConfig,
        mappingNotes,
        manualMappingBlockBypassed: manualMappingBlockBypassed || undefined,
      },
      onError(error) {
        global.alert(error.message);
      },
    }).then(this.reloadAddressCoverage);
  };

  closeManualMappingBlockModal = () => {
    this.setState({ manualMappingBlockModalArgs: null });
  };

  proceedWithBypassedMapping = () => {
    const args = this.state.manualMappingBlockModalArgs;
    this.setState({ manualMappingBlockModalArgs: null });
    if (!args) return;
    this.updateProviderCoveragePoints({ ...args, manualMappingBlockBypassed: true });
  };

  toggleRequiredType = (utilityTypeId) => {
    const { utilityTypes, addressCoverage, dispatch } = this.props;

    const utilityType = utilityTypes.find(({ id }) => id === utilityTypeId);
    const requiredTypes = addressCoverage.requiredTypes.map(({ utilityTypeId }) => utilityTypeId);
    const isRemovingType = requiredTypes.includes(utilityTypeId);

    const newUtilityTypeIds = isRemovingType
      ? lodashWithout([utilityTypeId], requiredTypes)
      : lodashUniq([...requiredTypes, utilityTypeId]);

    const confirmMessage = `Are you sure you want to mark "${utilityType.label}" as ${
      isRemovingType ? 'not ' : ''
    }required?`;

    if (!global.confirm(confirmMessage)) {
      return;
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_UPDATE_REQUIRED_TYPES,
      payload: {
        addressId: addressCoverage.id,
        utilityTypeIds: newUtilityTypeIds,
      },
      onError(error) {
        global.alert(error.message);
      },
    }).then(this.reloadAddressCoverage);
  };

  onMapServiceAreaProvider = ({ utilityServiceAreaId, utilityTypeId, companyName, addressId }) => {
    this.setState({
      serviceAreaProviderMappingArgs: {
        utilityServiceAreaId,
        utilityTypeId,
        companyName,
        addressId,
      },
      serviceAreaProviderMappingIsAdding: false,
    });
  };

  onMapServiceAreaProviderSelected = (utilityProvider) => {
    const { dispatch } = this.props;
    const { serviceAreaProviderMappingArgs } = this.state;

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
      payload: {
        utilityServiceAreaId: serviceAreaProviderMappingArgs.utilityServiceAreaId,
        utilityProviderId: utilityProvider.id,
      },
      onSuccess: () => {
        this.reloadAddressCoverage();
        this.onCloseMapServiceAreaProvider();
      },
    });
  };

  onMapServiceAreaProviderAddNew = () => {
    this.setState({
      serviceAreaProviderMappingIsAdding: true,
    });
  };

  onMapServiceAreaProviderAddNewSubmit = (utilityProviderUpsertForm) => {
    const { dispatch } = this.props;
    const { serviceAreaProviderMappingArgs } = this.state;

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
      payload: utilityProviderUpsertForm,
    }).then(({ utilityProvider }) => {
      return dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_SERVICE_AREA_PROVIDER_MAPPING,
        payload: {
          utilityServiceAreaId: serviceAreaProviderMappingArgs.utilityServiceAreaId,
          utilityProviderId: utilityProvider.id,
        },
        onSuccess: () => {
          this.reloadAddressCoverage();
          this.onCloseMapServiceAreaProvider();
        },
      });
    });
  };

  onCloseMapServiceAreaProvider = () => {
    this.setState({
      serviceAreaProviderMappingArgs: null,
      serviceAreaProviderMappingIsAdding: false,
    });
  };

  reloadAddressCoverage = () => {
    const { dispatch, match } = this.props;

    return Promise.all([
      dispatch(UtilityProviderActions.getAddressCoverage, {
        addressId: parseInt(match.params.addressId, 10),
        refresh: true,
      }).then((addressCoverage) => {
        if (addressCoverage) {
          return Promise.all([
            dispatch(UtilityProviderActions.getMappedProvidersInRange, {
              latitude: addressCoverage.latitude,
              longitude: addressCoverage.longitude,
              rangeInMiles: UTILITY_PROVIDERS_MILES_RANGE,
              utilityTypeId: null,
            }),
            dispatch(UtilityProviderActions.getListByAddressId, {
              addressId: addressCoverage.id,
              refresh: true,
            }),
          ]);
        }
      }),
    ]);
  };

  onConfirmMapping = ({ addressId, utilityTypeId }) => {
    const { dispatch } = this.props;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_FCC_CONFIRM_MAPPING,
      payload: {
        addressId,
        utilityTypeId,
      },
      onSuccess: () => {
        this.reloadAddressCoverage();
      },
    });
  };

  render() {
    const {
      addressCoverage,
      getAddressCoverageApiState,
      utilityTypes,
      match,
      utilityProvidersInRange,
      utilityProvidersByAddress,
    } = this.props;

    const {
      serviceAreaProviderMappingArgs,
      serviceAreaProviderMappingIsAdding,
      manualMappingBlockModalArgs,
    } = this.state;

    if (getAddressCoverageApiState.inProgress && !addressCoverage) {
      return <div className="loading-ellipses" />;
    }

    if (!addressCoverage) {
      return <div>{`Address ID ${match.params.addressId} not found.`}</div>;
    }

    return (
      <>
        <SrUtilityProfitAdminProcessAddressPage
          utilityTypes={utilityTypes}
          addressCoverage={addressCoverage}
          updateProviderCoveragePoints={this.updateProviderCoveragePoints}
          toggleRequiredType={this.toggleRequiredType}
          utilityProvidersInRange={utilityProvidersInRange}
          utilityProvidersByAddress={utilityProvidersByAddress}
          onMapServiceAreaProvider={this.onMapServiceAreaProvider}
          onConfirmMapping={this.onConfirmMapping}
        />
        {serviceAreaProviderMappingArgs && (
          <UPProviderSearchForm
            utilityType={utilityTypes.find(
              ({ id }) => serviceAreaProviderMappingArgs.utilityTypeId === id,
            )}
            addressDisplay={serviceAreaProviderMappingArgs.companyName}
            onProviderSelected={this.onMapServiceAreaProviderSelected}
            onClose={this.onCloseMapServiceAreaProvider}
            onAddNew={this.onMapServiceAreaProviderAddNew}
          />
        )}
        {serviceAreaProviderMappingIsAdding && (
          <UPProviderUpsertForm
            title="Connect to a Provider"
            utilityType={utilityTypes.find(
              ({ id }) => serviceAreaProviderMappingArgs.utilityTypeId === id,
            )}
            initialValues={{
              addressId: serviceAreaProviderMappingArgs.addressId,
              utilityTypeId: serviceAreaProviderMappingArgs.utilityTypeId,
            }}
            addressDisplay={serviceAreaProviderMappingArgs.companyName}
            onCancel={this.onCloseMapServiceAreaProvider}
            onSubmit={this.onMapServiceAreaProviderAddNewSubmit}
          />
        )}
        {manualMappingBlockModalArgs && (
          <SrSimpleMessageModal
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            headline={getBlockedProviderTitle(
              manualMappingBlockModalArgs.blockedProviderIds,
              manualMappingBlockModalArgs.blockedProviderActions,
            )}
            message={
              <>
                {`${getBlockedProviderLabel(
                  manualMappingBlockModalArgs.blockedProviderIds,
                )} ${manualMappingBlockModalArgs.blockedProviderIds.length > 1 ? 'are' : 'is'} managed exclusively by the automated eligibility API.`}
                <br />
                Manual changes are not recommended.
              </>
            }
            onClose={this.closeManualMappingBlockModal}
            customCtas={[
              <div key="btns" style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    hasBoxShadow
                    onClick={this.proceedWithBypassedMapping}
                  >
                    Proceed anyway
                  </SrButton>
                </div>
                <div style={{ flex: 1 }}>
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_GREY}
                    hasBoxShadow
                    onClick={this.closeManualMappingBlockModal}
                  >
                    Cancel
                  </SrButton>
                </div>
              </div>,
            ]}
          />
        )}
      </>
    );
  }
}

UtilityProfitAdminProcessingAddressPage.displayName = 'UtilityProfitAdminProcessingAddressPage';

export default UtilityProfitAdminProcessingAddressPage;
